import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getActiveAnnouncements } from '../services/announcementsApi';
import { badgesApi } from '../services/badgesApi';
import useViewerIdentity from '../hooks/useViewerIdentity';
import BadgePrizeShowcase from './common/BadgePrizeShowcase';
import './AnnouncementBanner.css';

const AnnouncementBanner = () => {
  const { userId } = useViewerIdentity();
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [dismissedIds, setDismissedIds] = useState(() => {
    const saved = localStorage.getItem('dismissedAnnouncements');
    return saved ? JSON.parse(saved) : [];
  });
  const [expandedAnnouncements, setExpandedAnnouncements] = useState(new Set());
  const hasFetchedRef = useRef(false);
  const CONTENT_PREVIEW_LENGTH = 150;
  const isLoggedIn = userId !== null;

  const parseSeriesIds = (str) => {
    if (!str) return [];
    try {
      const arr = JSON.parse(str);
      return Array.isArray(arr) ? arr.map(Number).filter(n => !isNaN(n)) : [];
    } catch {
      return [];
    }
  };

  const currentSeriesIds = useMemo(() => {
    if (!isVisible || announcements.length === 0) return [];
    return parseSeriesIds(announcements[currentIndex]?.badgeSeriesIds);
  }, [isVisible, announcements, currentIndex]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchAnnouncements();
  }, []);

  useEffect(() => {
    if (announcements.length > 1 && !expandedAnnouncements.has(currentIndex)) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % announcements.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [announcements.length, currentIndex, expandedAnnouncements]);

  const fetchAnnouncements = async () => {
    try {
      const data = await getActiveAnnouncements();
      const activeAnnouncements = data.filter(
        (ann) => !dismissedIds.includes(ann.id)
      );
      setAnnouncements(activeAnnouncements);
      setIsVisible(activeAnnouncements.length > 0);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    }
  };

  const handleDismiss = (id) => {
    const newDismissedIds = [...dismissedIds, id];
    setDismissedIds(newDismissedIds);
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissedIds));
    
    const remainingAnnouncements = announcements.filter((ann) => ann.id !== id);
    setAnnouncements(remainingAnnouncements);
    
    if (remainingAnnouncements.length === 0) {
      setIsVisible(false);
    } else {
      setCurrentIndex((prev) => prev % remainingAnnouncements.length);
    }
  };

  const handleToggleExpand = (e) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedAnnouncements);
    if (newExpanded.has(currentIndex)) {
      newExpanded.delete(currentIndex);
    } else {
      newExpanded.add(currentIndex);
    }
    setExpandedAnnouncements(newExpanded);
  };

  const handleLinkClick = (e) => {
    e.stopPropagation();
    if (currentAnnouncement.linkUrl) {
      window.open(currentAnnouncement.linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleBannerClick = () => {
    if (currentAnnouncement.linkUrl) {
      window.open(currentAnnouncement.linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (!isVisible || announcements.length === 0) {
    return null;
  }

  const currentAnnouncement = announcements[currentIndex];
  const isExpanded = expandedAnnouncements.has(currentIndex);
  const hasContent = currentAnnouncement.content && currentAnnouncement.content.trim().length > 0;
  const shouldShowReadMore = hasContent && currentAnnouncement.content.length > CONTENT_PREVIEW_LENGTH;
  const displayContent = shouldShowReadMore && !isExpanded
    ? currentAnnouncement.content.slice(0, CONTENT_PREVIEW_LENGTH) + '...'
    : currentAnnouncement.content;

  return (
    <div
      className={`announcement-banner ${!hasContent ? 'announcement-banner--title-only' : ''} ${currentAnnouncement.linkUrl ? 'announcement-banner--clickable' : ''}`}
      onClick={handleBannerClick}
    >
      <div className="announcement-banner__content">
        <div className="announcement-banner__icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 11l19-9-9 19-2-8-8-2z" />
          </svg>
        </div>
        <div className="announcement-banner__text">
          <span className="announcement-banner__title">
            {currentAnnouncement.title}
          </span>
          {hasContent && (
            <span className={`announcement-banner__message ${isExpanded ? 'announcement-banner__message--expanded' : ''}`}>
              {displayContent}
            </span>
          )}
          {shouldShowReadMore && (
            <button
              className="announcement-banner__read-more"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpand(e);
              }}
            >
              {isExpanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
        <button
          className="announcement-banner__close"
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss(currentAnnouncement.id);
          }}
          aria-label="Dismiss announcement"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      {currentSeriesIds.length > 0 && (
        <div className="announcement-banner__prizes" onClick={(e) => e.stopPropagation()}>
          <BadgePrizeShowcase
            seriesIds={currentSeriesIds}
            compact
          />
        </div>
      )}
      {announcements.length > 1 && (
        <div className="announcement-banner__dots">
          {announcements.map((_, index) => (
            <button
              key={index}
              className={`announcement-banner__dot ${
                index === currentIndex ? 'announcement-banner__dot--active' : ''
              }`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to announcement ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnouncementBanner;
